import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Switch,
  Radio,
  RadioGroup,
  Checkbox,
  FormControlLabel,
  TextField,
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { useAuth } from '../../context/AuthContext';
import { AppProvider } from '@toolpad/core/AppProvider';
import { appTheme } from '../../theme/appTheme';
import DashboardNavigationBar from '../DashboardNavigationBar/DashboardNavigationBar';
import LoadingOverlay from '../common/LoadingOverlay';
import NkySnackbarAlert from '../common/NkySnackbarAlert/NkySnackbarAlert';
import { accountService } from '../../services/api/accountService';
import { businessService } from '../../services/api/businessService';
import { formatPhoneInputMask, phoneDigitsForApi } from '../../utils/phoneInputMask';
import {
  DashboardContainer,
  SidebarContainer,
  ContentWrapper,
} from '../BusinessDashboard/BusinessDashboard.styled';
import {
  PageWrapper,
  HeaderRow,
  PageHeading,
  SubHeading,
  SectionHeading,
  EmptyState,
  ActionErrorText,
} from '../ServiceRequests/ServiceRequests.styled';
import {
  DefaultNotice,
  ToggleRow,
  ToggleTitle,
  ToggleSubtitle,
  PhoneOptionCard,
  PhoneOptionDetail,
  FieldHint,
  ConsentCard,
  ConsentDescription,
  ConsentMeta,
  ConsentCheckboxRow,
  SaveButton,
  SaveHint,
} from './SmsNotifications.styled';

const BRAND_BLUE = '#0d4efe';
const BRAND_TEAL = '#01ebb2';

const SmsNotifications = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [business, setBusiness] = useState(null);

  const [smsEnabled, setSmsEnabled] = useState(false);
  const [phoneSource, setPhoneSource] = useState('business');
  const [customPhone, setCustomPhone] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const [formError, setFormError] = useState('');

  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');

  /* ─── Auth guard (mirrors ServiceRequests / BusinessDashboard) ─── */
  useEffect(() => {
    if (!user) return;
    if (user?.Role !== 'business_user' && user?.Role !== 'admin_user') {
      navigate('/');
    }
  }, [user, navigate]);

  /* ─── Load business + existing SMS preferences ─── */
  useEffect(() => {
    let isCancelled = false;

    const loadBusiness = async () => {
      setIsLoading(true);
      setError('');
      try {
        const account = await accountService.getAccount();
        if (isCancelled) return;

        if (!account.BusinessId) {
          setError('No business associated with your account.');
          return;
        }

        const businessData = await businessService.getBusinessForOwnerDashboard(
          account.BusinessId,
        );
        if (isCancelled) return;

        setBusiness(businessData);
        const sms = businessData.Sms ?? {};
        setSmsEnabled(Boolean(sms.LeadNotificationsEnabled));
        setPhoneSource(sms.LeadNotificationPhoneNumber ? 'custom' : 'business');
        setCustomPhone(
          formatPhoneInputMask(sms.LeadNotificationPhoneNumber || ''),
        );
        setConsentChecked(Boolean(sms.OptInConsentedAt));
      } catch (err) {
        if (!isCancelled) {
          setError(err.message || 'Could not load your business. Please try again.');
        }
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    };

    loadBusiness();
    return () => {
      isCancelled = true;
    };
  }, []);

  const businessPhoneFormatted = formatPhoneInputMask(business?.BusinessPhoneNumber || '');
  const hasBusinessPhone = Boolean(business?.BusinessPhoneNumber);
  const persistedSmsEnabled = Boolean(business?.Sms?.LeadNotificationsEnabled);
  const saveDisabled = isSaving || (!smsEnabled && !persistedSmsEnabled);

  const handleSave = async () => {
    setFormError('');

    if (smsEnabled) {
      if (phoneSource === 'business' && !hasBusinessPhone) {
        setFormError(
          'Add a phone number in your Business Dashboard first, or choose "Use a different phone number" below.',
        );
        return;
      }
      if (phoneSource === 'custom' && phoneDigitsForApi(customPhone).length !== 10) {
        setFormError('Enter a 10-digit phone number to receive text notifications.');
        return;
      }
      if (!consentChecked) {
        setFormError('Check the consent box to enable text notifications.');
        return;
      }
    }

    setIsSaving(true);
    setSaveError('');
    try {
      const payload = {
        Sms: {
          LeadNotificationsEnabled: smsEnabled,
          LeadNotificationPhoneNumber:
            smsEnabled && phoneSource === 'custom' ? phoneDigitsForApi(customPhone) : null,
          OptInConsentedAt: smsEnabled
            ? business?.Sms?.OptInConsentedAt || new Date().toISOString()
            : null,
        },
      };

      const updated = await businessService.editBusiness(business.BusinessId, payload);
      setBusiness((prev) => ({ ...prev, ...updated }));
      setSaveSuccess(true);
    } catch (err) {
      setSaveError(err.message || 'Could not save your SMS preferences. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AppProvider branding={{ title: 'SMS Notifications' }} theme={appTheme}>
      <DashboardContainer>
        <SidebarContainer>
          <DashboardNavigationBar />
        </SidebarContainer>

        <ContentWrapper>
          <PageWrapper>
            <HeaderRow>
              <div>
                <PageHeading>SMS Notifications</PageHeading>
                <SubHeading>
                  Choose how your business hears about new service requests.
                </SubHeading>
              </div>
            </HeaderRow>

            {error && (
              <EmptyState>
                <p>{error}</p>
              </EmptyState>
            )}

            {!error && business && (
              <>
                <DefaultNotice>
                  <EmailOutlinedIcon />
                  <span>
                    By default, new service requests are sent to your business email on file
                    {business.BusinessEmail ? (
                      <>
                        {' '}
                        (<strong>{business.BusinessEmail}</strong>)
                      </>
                    ) : null}
                    . Turn on SMS notifications below to also get a text message the
                    moment a customer submits a service request.
                  </span>
                </DefaultNotice>

                <ToggleRow>
                  <div>
                    <ToggleTitle>Enable SMS service-request notifications</ToggleTitle>
                    <ToggleSubtitle>
                      Get a text message for every new service request routed to your
                      business.
                    </ToggleSubtitle>
                  </div>
                  <Switch
                    checked={smsEnabled}
                    onChange={(e) => setSmsEnabled(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: BRAND_BLUE },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: BRAND_TEAL,
                      },
                    }}
                  />
                </ToggleRow>

                {smsEnabled && (
                  <>
                    <SectionHeading>Where should we text you?</SectionHeading>
                    <RadioGroup
                      value={phoneSource}
                      onChange={(e) => setPhoneSource(e.target.value)}
                    >
                      <PhoneOptionCard $selected={phoneSource === 'business'}>
                        <FormControlLabel
                          value="business"
                          control={<Radio sx={{ '&.Mui-checked': { color: BRAND_BLUE } }} />}
                          label={
                            <div>
                              <div>Use my business phone number on file</div>
                              <PhoneOptionDetail>
                                {hasBusinessPhone ? businessPhoneFormatted : 'No phone number on file'}
                              </PhoneOptionDetail>
                            </div>
                          }
                        />
                      </PhoneOptionCard>

                      <PhoneOptionCard $selected={phoneSource === 'custom'}>
                        <FormControlLabel
                          value="custom"
                          control={<Radio sx={{ '&.Mui-checked': { color: BRAND_BLUE } }} />}
                          label="Use a different phone number"
                        />
                        {phoneSource === 'custom' && (
                          <TextField
                            size="small"
                            type="tel"
                            value={customPhone}
                            onChange={(e) => setCustomPhone(formatPhoneInputMask(e.target.value))}
                            placeholder="555-123-4567"
                            inputProps={{ inputMode: 'numeric', autoComplete: 'tel' }}
                            sx={{ maxWidth: 260, ml: 4 }}
                          />
                        )}
                      </PhoneOptionCard>
                    </RadioGroup>

                    {phoneSource === 'business' && !hasBusinessPhone && (
                      <FieldHint>
                        Add a phone number in your <Link to="/dashboard">Business Dashboard</Link>{' '}
                        first, or choose "Use a different phone number" above.
                      </FieldHint>
                    )}

                    <SectionHeading>Text message consent</SectionHeading>
                    <ConsentCard>
                      <ConsentDescription>
                        By checking the box below and providing your phone number, you agree
                        to receive automated SMS messages from NKY Connect about new service
                        requests, inquiry alerts, and account notifications at the phone
                        number selected above.
                      </ConsentDescription>
                      <ConsentMeta>
                        <strong>Message Frequency:</strong> Message frequency varies based on
                        your service-request volume.
                      </ConsentMeta>
                      <ConsentMeta>
                        <strong>Standard Rates:</strong> Message and data rates may apply
                        depending on your mobile phone service plan.
                      </ConsentMeta>
                      <ConsentMeta>
                        <strong>Help &amp; Stop:</strong> Reply HELP for help, or STOP to
                        cancel text notifications at any time.
                      </ConsentMeta>
                      <ConsentMeta>
                        We will not share mobile information with third parties for
                        promotional or marketing purposes.
                      </ConsentMeta>
                      <ConsentMeta>
                        By checking the box below and saving, you agree to receive text
                        messages from NKY Connect as described above. See our{' '}
                        <Link to="/sms-terms" target="_blank" rel="noopener noreferrer">
                          SMS Terms
                        </Link>
                        ,{' '}
                        <Link to="/business-terms" target="_blank" rel="noopener noreferrer">
                          Business Terms of Service
                        </Link>
                        , and{' '}
                        <Link to="/privacy" target="_blank" rel="noopener noreferrer">
                          Privacy Policy
                        </Link>
                        . Consent is not required to use NKY Connect.
                      </ConsentMeta>

                      <ConsentCheckboxRow>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={consentChecked}
                              onChange={(e) => setConsentChecked(e.target.checked)}
                              sx={{ '&.Mui-checked': { color: BRAND_BLUE } }}
                            />
                          }
                          label="Yes, I agree to receive SMS service-request notifications from NKY Connect."
                        />
                      </ConsentCheckboxRow>
                    </ConsentCard>
                  </>
                )}

                {formError && <ActionErrorText>{formError}</ActionErrorText>}

                <SaveButton variant="contained" onClick={handleSave} disabled={saveDisabled}>
                  {isSaving ? 'Saving…' : 'Save SMS Preferences'}
                </SaveButton>

                {!isSaving && saveDisabled && (
                  <SaveHint>
                    Turn on SMS service-request notifications above to save preferences.
                  </SaveHint>
                )}
              </>
            )}
          </PageWrapper>
        </ContentWrapper>
      </DashboardContainer>

      {isLoading && <LoadingOverlay />}

      <NkySnackbarAlert
        open={saveSuccess}
        autoHideDuration={6000}
        onClose={() => setSaveSuccess(false)}
        severity="success"
        message="SMS preferences saved successfully!"
      />

      <NkySnackbarAlert
        open={!!saveError}
        autoHideDuration={6000}
        onClose={() => setSaveError('')}
        severity="error"
        message={saveError || ''}
      />
    </AppProvider>
  );
};

export default SmsNotifications;
